import React, { Component } from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import PropTypes from 'prop-types';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _debounce from 'lodash/debounce';
import {createSearchQuery} from '../lib/createSearchQuery';
import {addressFromRoute, commaFormatNumber} from '../lib/formatting';
import {
  dataLayerPush,
  getPageData,
  standardPropertyItem,
  trackShowFilters,
  trackSaveSearch,
} from '../lib/tracking';
import {trackLiveability} from '../components/Liveability/tracking';
import styles from './PropertySearch.scss';
import CollectionModal from '../components/Modal/CollectionModal/CollectionModal';
import {
  doSearch,
  getSearchImages,
  collectionModalClose,
  fetchSuburb,
  fetchMarketTrends,
} from '../actions';
import {doPropertySearch} from '../sagas/searchProperties';
import {getSurroundingSuburbs} from '../sagas/surroundingSuburbs';
import {getMarketTrends} from '../sagas/marketTrends';
import {fetchSuburbDetail} from '../sagas/SuburbDetail';
import {searchResults, searchPropertiesFallbackResults} from '../selectors/searchResults';
import SearchBox from '../components/PropertySearch/SearchBox';
import PropertyCardSearch from '../components/PropertyCard/PropertyCardSearch';
import Loading from '../components/Loading/Loading';
import Pagination from '../components/Pagination/Pagination';
import PropertySearchMap from '../components/SearchResultsMap/SearchResultsMap';
import AdSlot from '../components/AdSlot/AdSlot';
import FooterSmall from '../components/Footer/FooterSmall';
import {
  popularFeaturesForSearch,
  boolSearchParams as boolParams,
  saleDateOptions,
  breakpointMdHigh, saleTypeForSearch,
} from '../lib/config';
import MoreFilters from '../components/PropertySearch/MoreFilters';
import { createSearchURL } from '../lib/createSearchURL';
import SortBy from '../components/PropertySearch/SortBy';
import { Helmet } from 'react-helmet';
import PagedSEO from '../components/Pagination/PagedSEO';
import {formatAddressTargeting} from '../lib/adFunctions';
import FooterSEOLinks from '../components/Footer/FooterSEOLinks';
import Disclaimer from '../components/Disclaimer/Disclaimer';
import {getUniqueStateFromPropertyList} from '../lib/propertyFunctions';
import SavedSearchButton from '../components/SavedSearch/SavedSearchButton.js';
import moment from 'moment';
import SuburbBlurb from '../components/SuburbProfile/SuburbBlurb';

export const createBasicParams = (props) => {
  let basicParams = _cloneDeep(props.match.params);
  if (props.location.search !== '') {
    let urlParams = props.location.search.replace('?','').split('&');
    urlParams.map((token) => {
      token = token.replace(/%2c/igm, ',');
      let keyValue = token.split('=');
      let setVal = true;
      if (boolParams.includes(keyValue[0])) {
        if (['true', '', undefined].includes(keyValue[1])) {
          keyValue[1] = true;
        }
        else setVal = false;
      }

      if (setVal) {
        basicParams[keyValue[0]] = keyValue[1];
      }
      else delete basicParams[keyValue[0]];
    });
  }

  basicParams.additionalFeatures = {};
  [...Object.keys(popularFeaturesForSearch), 'isNewBuild','isEstablishedHome', 'featureFreeText'].forEach((key) => {
    if(key === 'featureFreeText' && !_isEmpty(basicParams['featureFreeText'])) {
      basicParams.additionalFeatures['featureFreeText'] = basicParams['featureFreeText'].split(',').map((text) => decodeURIComponent(text));
    } else if(basicParams[key] === 'true' || basicParams[key] === true) {
      basicParams.additionalFeatures[key] = true;
    }
    delete basicParams[key];
  });

  if(['real-estate', 'property'].includes(basicParams.searchType.toLowerCase())) {
    delete basicParams.excludeUnderOffer;
  }

  if(['property-for-sale', 'property-for-rent'].includes(basicParams.searchType.toLowerCase())) {
    delete basicParams.soldHistory;
    delete basicParams.rentedHistory;
  }

  if (!moment(basicParams.dateAvailableFrom, 'YYYY-MM-DD', true).isValid()) {
    delete basicParams.dateAvailableFrom;
  }

  basicParams.saleTypes = [];
  if(['property-for-sale'].includes(basicParams.searchType.toLowerCase())) {
    [...Object.keys(saleTypeForSearch)].forEach((key) => {
      if(basicParams[key] === 'true' || basicParams[key] === true) {
        basicParams.saleTypes.push(saleTypeForSearch[key]);
      }
      delete basicParams[key];
    });
  } else {
    delete basicParams.saleTypes;
  }

  return basicParams;
};

// These are the five ads that appear in the search results
const adIDs = ['21913510585','21913602542','21913602545','21913602548','21913602551'];
const adEveryNCards = 5; // every 5th card is an ad
const resultPerPageDesktop = 24;
const resultPerPageMobile = 25;

const detectDevice = () => {
  // these breakpoints match the rules in "src\assets\styles\_misc-variables.scss"
  let w = window.innerWidth;
  let d = 'desktop';
  if (w <= 1366) d = 'tablet';
  if (w < 768) d = 'phone';
  return d;
};

const trackSearchDisplay = (searchResults) => {
  if (searchResults.length > 0) {
    let pd = getPageData();
    let view_item_list = {
      event: 'view_item_list',
      ecommerce: {
        items: [],
      },
      page_type: pd.type,
      page_name: pd.name,
      item_list_id: 'property',
      item_list_name: 'property',
    };
    searchResults.map((property) => {
      if (property && property.address) view_item_list.ecommerce.items.push(standardPropertyItem(property, property?.listing?.auction?.saleType));
    });
    dataLayerPush(view_item_list);

    // liveability impressions
    searchResults.map((property) => {
      if (property.liveability && property.liveability.livebilityIndex >= 8) trackLiveability('liveability_search_impression', property);
    });
  }
};

const getMobileAdSlotView = (index, searchParams) => {
  if (typeof window !== 'undefined' && window.innerWidth <= breakpointMdHigh && ((index+1) % adEveryNCards) === 0) {
    return <div style={{height: '250px'}} className={'d-flex justify-content-center align-items-center mb-3 ' + styles.resultSlot}>
      <AdSlot adId={adIDs[((index+1)/adEveryNCards)-1]} pageTargeting={formatAddressTargeting(searchParams?.query?.queries?.[0])}/>
    </div>;
  }
};

const getResultPerPage = () => {
  if (typeof window !== 'undefined' && window.innerWidth <= breakpointMdHigh) {
    return resultPerPageMobile;
  }

  return resultPerPageDesktop;
};


const EmptySearchResults = (props) => {
  const {
    fallbackStatus,
    fallbackResults,
    activePropertyStatus,
    isFilterRemoved,
    searchAddress,
  } = props;

  const getPropertyCategoryText = () => {
    if (activePropertyStatus === 'off market')
      return 'off market properties';

    if (['for sale', 'for rent'].includes(activePropertyStatus))
      return `properties ${activePropertyStatus}`;

    return 'properties';
  };

  if (fallbackStatus === 'success') {
    if (!!fallbackResults.content.length) {
      return <React.Fragment>
        <div className="m-3">
          <div className="text-center lgText bold600">We didn&apos;t find any {getPropertyCategoryText()} that match your search criteria</div>
          <div className="text-center mt-2 mb-3">Here are some {isFilterRemoved ? 'other' : 'similar'} {getPropertyCategoryText()} in the surrounding areas.</div>
        </div>
        <div className={styles.resultGrid}>
          {fallbackResults.content.map((noFilterResult, index) =>
            <React.Fragment key={index}>
              {getMobileAdSlotView(index, searchAddress)}
              <div className={styles.resultSlot}>
                <PropertyCardSearch property={noFilterResult} />
              </div>
            </React.Fragment>
          )}
        </div>
      </React.Fragment>;
    }
    return <React.Fragment>
      <div className="text-center lgText bold600">We didn&apos;t find any {getPropertyCategoryText()} that match your search criteria</div>
      <div className="text-center mt-2 mb-5">Try starting your search in neighbouring areas or changing your search type.</div>
    </React.Fragment>;
  }
  return null;
};

const getPropertyStatus = (propertyStatus) => {
  switch(propertyStatus) {
    case 'property':
      return 'off market';
    case 'for-sale':
    case 'property-for-sale':
      return 'for sale';
    case 'for-rent':
    case 'property-for-rent':
      return 'for rent';
    case 'sold':
      return 'sold';
    default:
      return '';
  }
};

const sanitizeSuburb = (suburb) => suburb?.replace(/--/g, '@')?.replace(/-/g, ' ')?.replace(/@/g, '-');

class PropertySearch extends Component {
  constructor(props) {
    super(props);
    // combine params from route and queryparams
    this.basicParams = createBasicParams(this.props);
    // now munge it into something we can post
    this.searchParams = createSearchQuery(this.basicParams, getResultPerPage());
    this.toggleText = 'Map';
    this.state = {
      showMobileMap: false,
      targetDevice: null,
      moreFiltersOpen: false,
      activePropertyStatus: getPropertyStatus(this.basicParams.searchType),
      isAdVisible: false,
      pageLoaded: false,
      showAds: false,
    };
    this.debounce = null;
    this.mounted = true;
    // device detection - we are rendering depending on what device the user is on
    if (this.props.staticContext && this.props.staticContext.devicetype) this.state.targetDevice = this.props.staticContext.devicetype; // for ssr
    else if (typeof window !== 'undefined' && window.hasOwnProperty('oth_device')) this.state.targetDevice = window.oth_device; // for client
  }

  static preloadSaga = () => {
    return [doPropertySearch, getSurroundingSuburbs, fetchSuburbDetail, getMarketTrends];
  };

  getSuburbAndMarketTrends = () => {
    if (!_isEmpty(_get(this.searchParams, 'query.queries[0].suburb'))) {
      const {suburb, postCode: postcode, stateCode} = this.props.match.params;
      const defaultYear = 10;
      const defaultPropertyType = 'Both';
      this.props.fetchSuburb({suburb, postcode});
      /* todo: fix suburb rule somewhere deeper without affecting other components which calls trends data
          *    partially formatted as fetchMarketTrends have a similar rule, fully formatting the suburb here
          *    would format the suburb twice. only spaces fail in this scenario.
          */
      const partiallyFormattedSuburb = suburb.replace(/--/g, '@').replace(/-/g, ' ').replace(/@/g, '--');
      this.props.fetchMarketTrends({
        suburb: partiallyFormattedSuburb,
        postcode,
        stateCode,
        defaultYear,
        defaultPropertyType
      });
    }
  }

  componentDidMount() {
    this.setState({showAds: true, pageLoaded: true});
    // device detection
    let targetDevice = this.state.targetDevice;
    if (!this.state.targetDevice && window.oth_device) targetDevice = window.oth_device; // not ssr
    this.debounce = _debounce(() => {if (this.mounted) this.setState({targetDevice: detectDevice()});}, 150);
    window.addEventListener('resize', this.debounce);

    // some logic here to prevent duplicate calls
    let newSearch = false;

    this.setState({targetDevice: targetDevice });

    if (!_get(this.props, 'propertySearch.results.payload') || !_isEqual(this.searchParams?.query?.queries, this.props.propertySearch.results.payload?.query?.queries)) newSearch = true;

    if (newSearch) {
      this.props.doSearch(this.searchParams);
    }
    this.getSuburbAndMarketTrends();

    // get the carousel images, only if the search exist when we're mounting
    if (_get(this.props, 'propertySearch.results.content')) this.props.getSearchImages({searchResults: this.props.propertySearch.results.content});

    if (!newSearch && _get(this.props, 'propertySearch.status') && this.props.propertySearch.status === 'success') {
      // track the initial search display
      trackSearchDisplay(this.props.propertySearch.results.content);
    }
  }

  componentDidUpdate(prevProps) {
    let newSearch = false;
    if (this.props.propertySearch.status === 'success' && prevProps.location.key !== this.props.location.key) {
      newSearch = true;
    }
    const currentActivePropertyStatus = getPropertyStatus(this.basicParams.searchType);
    if(currentActivePropertyStatus !== this.state.activePropertyStatus) {
      this.setState({activePropertyStatus: currentActivePropertyStatus});
    }
    if (newSearch) {
      this.basicParams = createBasicParams(this.props);
      this.searchParams = createSearchQuery(this.basicParams, getResultPerPage());
      this.props.doSearch(this.searchParams);

      this.getSuburbAndMarketTrends();
    }

    // ensure map is displayed/hidden as expected based on url
    if ((this.props.location.hash === '#map' && !this.state.showMobileMap)
      || (this.props.location.hash !== '#map' && this.state.showMobileMap)) {
      this.toggleMap();
    }
    // tracking
    if (!newSearch && prevProps.propertySearch.status !== 'success' && this.props.propertySearch.status === 'success') {
      // we have a new search to display so track it
      trackSearchDisplay(this.props.propertySearch.results.content);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener('resize', this.debounce);
  }

  getCurrentURL = () => {
    return this.props.location.pathname + this.props.location.search;
  }
  toggleMap = () => {
    if (!this.state.showMobileMap) {
      this.toggleText = 'List';
      let url = this.getCurrentURL() + '#map';
      this.props.history.push(url);
    }
    else {
      this.toggleText = 'Map';
      this.props.history.push(this.getCurrentURL());
    }
    this.setState({showMobileMap: !this.state.showMobileMap});
  }

  submitSearch = () => {
    let searchURL = createSearchURL(this.props.filterSearch);
    this.props.history.push(searchURL);
  }

  toggleMoreFilters = (e, source) => {
    e.preventDefault();
    if(source === 'external' && e.target.id === 'more-filters-toggle') {
      return;
    }
    if (!this.state.moreFiltersOpen) trackShowFilters(this.props.filterSearch);
    this.setState({moreFiltersOpen: !this.state.moreFiltersOpen});
  }

  totalPropertiesLabel = () => {
    const totalProperties = this.props.propertySearch?.results?.totalElements;
    const includeSurroundSuburbs = this.basicParams.includeSurroundSuburbs ? 'and surrounding areas' : '';
    const searchLocation = `${addressFromRoute(this.props.match.params, this.props.location.search)} ${includeSurroundSuburbs}`;

    if (totalProperties !== 1) {
      return `${commaFormatNumber(totalProperties)} ${this.state.activePropertyStatus || ' for sale, for rent, and off market'} properties in ${searchLocation}`;
    }
    return `1 property found in ${searchLocation}`;
  }

  isSmallerThanTablet = () => typeof window != 'undefined' && window.innerWidth < 1024; // taken from .mobileLayout media screen

  renderResults = (results, device) => {
    return (
      <div className={styles.resultGrid}>
        {results.content.map((result, index) =>
          <React.Fragment key={index}>
            {device.includes(this.state.targetDevice) && this.state.showAds && ((index+1) % adEveryNCards) === 0 ?
              getMobileAdSlotView(index, this.searchParams)
              : null}
            <div className={styles.resultSlot}>
              <PropertyCardSearch property={result} />
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }

  render() {
    const {status, results} = this.props.propertySearch;
    const {
      status: fallbackStatus,
      results: fallbackResults,
      isFilterRemoved,
    } = this.props.propertySearchFallback;
    const { suburb, stateCode, postCode } = this.props.match.params;

    let bp = createBasicParams(this.props);
    const {activePropertyStatus} = this.state;

    return (
      <React.Fragment>
        {(!results || results.content.length === 0) && (!fallbackResults || fallbackResults.content.length === 0) ?
          <Helmet>
            <meta name="robots" />
          </Helmet> :
          <PagedSEO lastPage={results.totalPages} />
        }

        {/* mobile layout only */}
        <div className={styles.mobileLayout}>

          {/* the list view */}
          <div className={this.state.showMobileMap ? ' d-none' : ' d-block'}>
            <div className="p-3"><SearchBox searchParams={bp} currentSearch={this.searchParams} /></div>
            {status === 'success' &&
              <div className="d-flex flex-column w-100 pl-3 pr-3 pb-2">
                <div className="mb-0">
                  <h1 className={styles.resultCount}>
                    {this.totalPropertiesLabel()}
                  </h1>
                </div>
                <div className="d-flex w-100 align-items-center">
                  <div className="flex-grow-1"><div id="Sort1" className="d-inline-block"><SortBy /></div></div>
                  {this.isSmallerThanTablet() && // remove element from d-none to avoid rendering NewFeatureIndicator
                    <div><SavedSearchButton searchParams={this.searchParams} /></div>
                  }
                </div>
              </div>
            }
            {results && !!results.content.length &&
              this.renderResults(results, ['phone', 'tablet'])
            }
            {(!results || !results.content.length) &&
              fallbackStatus !== 'success' ?
              <Loading/> :
              <EmptySearchResults
                fallbackStatus={fallbackStatus}
                fallbackResults={fallbackResults}
                activePropertyStatus={activePropertyStatus}
                isFilterRemoved={isFilterRemoved}
                searchAddress={this.searchParams?.query?.queries?.[0]}
              />
            }
            {results?.totalPages > 1 &&
              <div>
                <Pagination totalPages={results.totalPages} pageParam="page" />
              </div>
            }
            { this.props.suburbInfo.status === 'success' &&
              <SuburbBlurb onSrp={true} propertyStatus={this.state.activePropertyStatus} />
            }
            <div className="m-3">
              <Disclaimer stateCodeList={getUniqueStateFromPropertyList(results?.content)}/>
            </div>
            { status === 'success' &&
                <FooterSEOLinks suburb={sanitizeSuburb(suburb)} state={stateCode} postCode={postCode} isWrap={true}/>
            }
            <div className={styles.footerWrap + (results && results.content.length === 0 ? ' ' + styles.noResultsFooter : '')}><FooterSmall /></div>
          </div>

          {/* the map view */}
          {this.state.showMobileMap &&
            <div className={styles.mapView + ' d-block'}>
              <div className={styles.mapWrap}><PropertySearchMap /></div>
            </div>
          }

          {/* map toggle */}
          {results?.content.length &&
            <div onClick={this.toggleMap} className={'d-flex justify-content-center align-items-center ' + styles.mapToggle + ' ' + styles['mapShowing-' + this.state.showMobileMap]}>
              <div>{this.toggleText}</div>
            </div>
          }

        </div>

        <div className={'w-100 '+ styles.desktopLayout}>
          <SearchBox searchParams={bp} currentSearch={this.searchParams} toggleMoreFilters={this.toggleMoreFilters} moreFiltersOpen={this.state.moreFiltersOpen}/>
          <div className={`d-flex ${styles.desktopViewWithHeaderAds}`}>
            <div className={'d-flex flex-column flex-grow-1 ' + styles.leftPanel}>

              {(this.state.targetDevice !== 'tablet') &&
                <div>{/* desktop and everything except mobile and tablet, search is fixed outside scroll area */}
                  <div className="d-flex align-items-center w-100 pt-0 pl-3 pr-3 pb-2">
                    {status === 'success' &&
                      <React.Fragment>
                        <div className="flex-grow-1">
                          <div className="mb-0">
                            <h1 className={styles.resultCount}>
                              {this.totalPropertiesLabel()}
                            </h1>
                          </div>
                          {results && results.totalElements ? <div id="Sort2" className="d-inline-block"><SortBy /></div> : null}
                        </div>
                        {!this.isSmallerThanTablet() &&
                          <div><SavedSearchButton searchParams={this.searchParams} /></div>
                        }
                      </React.Fragment>
                    }
                  </div>
                </div>}

              <div className={'d-flex flex-column flex-grow-1 pt-2 position-relative ' + styles.resultPanel}>
                {status === 'fetching' ? <Loading context="container" /> :
                  <div>
                    {(this.state.targetDevice === 'tablet') &&
                      <div className="d-flex flex-column w-100 pl-3 pr-3 pb-2"> {/* tablet only, search is fixed outside scroll area */}
                        <div className="mb-0">
                          <h1 className={styles.resultCount}>
                            {this.totalPropertiesLabel()}
                          </h1>
                        </div>
                        <div className="d-flex w-100">
                          <div className="flex-grow-1"><div id="Sort3" className="d-inline-block"><SortBy /></div></div>
                          <div><SavedSearchButton searchParams={this.searchParams} /></div>
                        </div>
                      </div>
                    }

                    {results && !!results.content.length &&
                  this.renderResults(results, ['desktop'])
                    }
                    {(!results || !results.content.length) &&
                  <div className="mt-2 mb-5">
                    {fallbackStatus !== 'success' ?
                      <Loading /> :
                      <EmptySearchResults
                        fallbackStatus={fallbackStatus}
                        fallbackResults={fallbackResults}
                        activePropertyStatus={activePropertyStatus}
                        isFilterRemoved={isFilterRemoved}
                        searchAddress={this.searchParams?.query?.queries?.[0]}
                      />
                    }
                  </div>
                    }
                    {results?.totalPages > 1 &&
                      <div>
                        <Pagination totalPages={results.totalPages} pageParam="page" />
                      </div>
                    }
                    {this.props.suburbInfo.status === 'success' &&
                      <SuburbBlurb onSrp={true} propertyStatus={this.state.activePropertyStatus} />
                    }
                    <div className={'d-block d-xl-none ' + styles.footerWrap + (results && results.content.length === 0 ? ' ' + styles.noResultsFooter : '')}><FooterSmall /></div>
                  </div>}
                <div className="m-3">
                  <Disclaimer stateCodeList={getUniqueStateFromPropertyList(results?.content)}/>
                </div>
                { status === 'success' &&
                    <FooterSEOLinks suburb={sanitizeSuburb(suburb)} state={stateCode} postCode={postCode} isWrap={true}/>
                }
              </div>
            </div>
            <div className={'flex-grow-1 ' + styles.rightPanel}>
              <PropertySearchMap />
              { this.state.moreFiltersOpen && (
                <React.Fragment>
                  <div className={styles.mapFilterOverlay} />
                  <MoreFilters searchFunction={this.submitSearch} isOpen={this.state.moreFiltersOpen} toggleFunction={this.toggleMoreFilters}/>
                </React.Fragment>
              ) }
            </div>
          </div>
        </div>
        <CollectionModal showModal={this.props.collections.modal === 'open'} toggleModal={this.props.collectionModalClose} property={this.props.collections.selectedProperty}/>
      </React.Fragment>
    );
  }
}

PropertySearch.propTypes = {
  match: PropTypes.object,
  location: PropTypes.object,
  history: PropTypes.object,
  propertySearch: PropTypes.object,
  propertySearchFallback: PropTypes.object,
  filterSearch: PropTypes.object,
  doSearch: PropTypes.func.isRequired,
  getSearchImages: PropTypes.func,
  userFavourites: PropTypes.object,
  collectionModalClose: PropTypes.func,
  fetchSuburb: PropTypes.func,
  fetchMarketTrends: PropTypes.func,
  staticContext: PropTypes.object,
  suburbInfo: PropTypes.object,
  collections: PropTypes.object,
  searchSaved: PropTypes.bool,
};

EmptySearchResults.propTypes = {
  fallbackStatus: PropTypes.string,
  fallbackResults: PropTypes.object,
  activePropertyStatus: PropTypes.string,
  isFilterRemoved: PropTypes.bool,
  searchAddress: PropTypes.object,
};

const mapStateToProps = (state) => {
  return {
    propertySearch: searchResults(state.searchProperties),
    propertySearchFallback: searchPropertiesFallbackResults(state.searchPropertiesFallback),
    filterSearch: state.filterSearch,
    savedSearches: state.userSavedSearches.savedSearches,
    userFavourites: state.userFavourites,
    collections: state.collections,
    suburbInfo: state.suburb,
  };
};

const mapDispatchToProps = {doSearch, getSearchImages, collectionModalClose, fetchSuburb, fetchMarketTrends};
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(PropertySearch));
